import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { SubmitMessageContext, ChatSessionInputContext } from '@/components/Chat/context';
import { useSpeechRecognition } from '@/components/Chat/hooks/useSpeechRecognition';
import { ChatStateDispatchContext } from '@/components/Chat.State/context';
import { TextToSpeechStateContext, StopTextToSpeechContext, VoiceModeContext } from '@/containers/TextToSpeechAudioPlayer';
import { capitalize } from '@/utils';
import { useTimeout } from '@/utils/hooks';
import { InputControlsContext } from './context';

type Props = {
  children: React.ReactNode;
};

export const InputControlsContainer = (props: Props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>();
  const soundLevelRef = useRef<HTMLDivElement>();
  const input = useContext(ChatSessionInputContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const stopTextToSpeech = useContext(StopTextToSpeechContext);
  const audioState = useContext(TextToSpeechStateContext);
  const voicemode = useContext(VoiceModeContext);
  const { canSubmit, onSubmit: handleSubmit } = useContext(SubmitMessageContext);

  const handleVoiceModeAutoSubmit = useCallback(() => {
    if (voicemode?.enabled && canSubmit) {
      handleSubmit();
    }
  }, [
    handleSubmit,
    voicemode,
    canSubmit,
  ]);

  const [_, setTimer, clearTimer] = useTimeout(handleVoiceModeAutoSubmit, 300);

  const onSpeechFinished = useCallback((value: string, prefix: string) => {
    let newVal = prefix + ' ' + capitalize(value);

    if (!endsWithPunctuation(newVal)) {
      newVal = newVal + '?';
    }

    dispatch({
      type: 'input-changed',
      payload: { value: newVal },
    });

    setTimer();
  }, [
    dispatch,
    setTimer,
  ]);

  const onAudioLevelChange = useCallback((level: number) => {
    if (!soundLevelRef.current) {
      console.log('no ref');
    }
    soundLevelRef.current?.setAttribute('style', `height: ${Math.min(20, Math.max(level, 4))}px`);
  }, []);

  const onInterimResult = useCallback((result: string, prefix: string) => {
    const value = prefix + ' ' + capitalize(result);
    dispatch({
      type: 'input-changed',
      payload: { value },
    });
    clearTimer();
  }, [dispatch, clearTimer]);

  const { start, stop, listening, isSupported: speechRecogSupported, micState, errorMsg, interimResult } = useSpeechRecognition({
    timeout: voicemode.enabled ? 3000 : undefined,
    onResult: onSpeechFinished,
    onInterimResult,
    onAudioLevelChange,
    voicemode: voicemode.enabled,
  });

  const onVoiceClick = useCallback(() => {
    if (listening) {
      clearTimer();
      stop();
    } else {
      start(input);
      textAreaRef.current.addEventListener('focus', () => {
        clearTimer();
        stop();
      });
    }
  }, [input, listening, start, stop, clearTimer]);

  const handleSubmitButtonClick = useCallback(() => {
    clearTimer();
    if (listening) {
      stop();
    }

    handleSubmit();
  }, [handleSubmit, listening, stop, clearTimer]);

  const handleVoiceModeClick = useCallback(() => {
    clearTimer();

    if (voicemode.enabled && listening) {
      stop();
    } else if (!voicemode.enabled && !listening && audioState.state !== 'playing') {
      start(input);
      textAreaRef.current.addEventListener('focus', () => {
        clearTimer();
        stop();
      });
    }

    voicemode.toggle();
  }, [
    audioState,
    input,
    clearTimer,
    listening,
    start,
    stop,
    voicemode,
  ]);

  const shouldStopPlayback = useMemo(() => {
    return audioState.state === 'playing' && voicemode.enabled && listening;
  }, [
    audioState.state,
    listening,
    voicemode.enabled,
  ]);

  useEffect(() => {
    if (shouldStopPlayback) {
      stopTextToSpeech();
    }
  }, [
    shouldStopPlayback,
    stopTextToSpeech,
  ]);

  const value = {
    onClickSubmit: handleSubmitButtonClick,
    onClickMicrophone: onVoiceClick,
    onClickVoiceMode: handleVoiceModeClick,
    speechRecognition: {
      errorMsg,
      interimResult,
      isSupported: speechRecogSupported,
      listening,
      micState,
      start,
      stop,
    },
    soundLevelRef,
    textAreaRef,
  };

  return (
    <InputControlsContext.Provider value={value}>
      {props.children}
    </InputControlsContext.Provider>
  );
};

InputControlsContainer.displayName = 'InputControls.Container';

function endsWithPunctuation(str: string) {
  const punctuation = /[.,!?;:'"-]/;
  return punctuation.test(str.charAt(str.length - 1));
}